import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { CopyCheckRead, PlatformName } from '../../types/api';
import { checkCopy } from './knowledgeBaseApi';

/** Long enough to stop firing per keystroke, short enough to feel live. */
export const COPY_CHECK_DEBOUNCE_MS = 400;

export interface CopyCheckResult {
  /** The check for the current platform + text, once it has landed. */
  result: CopyCheckRead | null;
  error: string | null;
  /** There is text on screen the current result does not describe yet. */
  isChecking: boolean;
  /** At least one finding is a `fail` — what the pipeline guardrail rejects on. */
  hasFailure: boolean;
}

/** Result of the last completed check, tagged with exactly what produced it. */
interface CheckedCopy {
  businessId: string;
  /** Platform + text the result belongs to; stale responses never match. */
  key: string;
  result: CopyCheckRead | null;
  error: string | null;
}

function checkKey(platform: PlatformName, text: string): string {
  // NUL separates the two halves: it cannot occur in typed copy, so no text can
  // impersonate a different platform's key.
  return `${platform} ${text}`;
}

/**
 * Runs text past the deterministic copy guardrails as it is typed — never-say
 * terms, the platform character limit, PII patterns. No model call and no quota
 * spent, which is why it can run live.
 *
 * The last completed response is stored tagged with the business, platform and
 * text that produced it and matched during render, so an out-of-order response
 * for older text is ignored instead of overwriting the current one. Empty text
 * checks nothing: there is no finding to make about a caption nobody wrote.
 */
export function useCopyCheck(
  businessId: string,
  platform: PlatformName,
  text: string,
  debounceMs: number = COPY_CHECK_DEBOUNCE_MS,
): CopyCheckResult {
  const [checked, setChecked] = useState<CheckedCopy | null>(null);

  const hasText = text.trim().length > 0;
  const key = checkKey(platform, text);

  useEffect(() => {
    if (!hasText) return;

    const controller = new AbortController();
    let cancelled = false;

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await checkCopy(businessId, { text, platform }, controller.signal);
          if (cancelled) return;
          setChecked({ businessId, key, result, error: null });
        } catch (caught) {
          if (cancelled || controller.signal.aborted) return;
          setChecked({
            businessId,
            key,
            result: null,
            error: getErrorMessage(caught, 'The copy check did not run. Try again.'),
          });
        }
      })();
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [businessId, debounceMs, hasText, key, platform, text]);

  const isCurrent = checked !== null && checked.businessId === businessId && checked.key === key;
  const result = isCurrent ? checked.result : null;

  return {
    result,
    error: isCurrent ? checked.error : null,
    isChecking: hasText && !isCurrent,
    hasFailure: result?.findings.some((finding) => finding.severity === 'fail') ?? false,
  };
}
